import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AccountHistoricBalance } from "@ui-kit/components/index.es";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { arrayOf, bool, func, number, shape, string } from "prop-types";

import PageLoading from "pages/_components/PageLoading";
import { resizableRoute } from "pages/_components/Resizable";

import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as layoutActions } from "reducers/layout";
import { selectors as sessionSelectors } from "reducers/session";

import { maskedAccountNumber, productTypes } from "util/accounts";
import { getIsoCurrency } from "util/deposits";
import * as i18n from "util/i18n";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getBankAccountDetailPath } from "util/v3UxConfig";
import {
    buildHistoricBalancePagination,
    buildQueryMeta,
    getDefaultDateRange,
    mapHistoricBalanceToRow,
} from "v3/pages/accounts/accountHistoricBalance";
import { getHistoricBalanceLabels } from "v3/v3UiKitI18n";

function AccountHistoricBalanceV3Page(props) {
    AccountHistoricBalanceV3Page.propTypes = {
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
        account: shape({}),
        fetching: bool.isRequired,
        fetchingBalance: bool.isRequired,
        fetchingDownload: bool.isRequired,
        loggedUser: shape({}).isRequired,
        match: shape({
            params: shape({
                id: string,
            }),
        }).isRequired,
        historicBalances: arrayOf(shape({})),
        historicBalancesTotalPages: number,
        historicBalancesPageNumber: number,
        dateFrom: shape({}),
        dateTo: shape({}),
    };

    AccountHistoricBalanceV3Page.defaultProps = {
        account: null,
        historicBalances: null,
        historicBalancesTotalPages: 0,
        historicBalancesPageNumber: 1,
        dateFrom: null,
        dateTo: null,
    };

    const {
        dispatch,
        isDesktop,
        account,
        fetching,
        fetchingBalance,
        fetchingDownload,
        loggedUser,
        match,
        historicBalances,
        historicBalancesTotalPages,
        historicBalancesPageNumber,
        dateFrom,
        dateTo,
    } = props;

    const accountId = match?.params?.id;
    const goBackPath = accountId != null ? getBankAccountDetailPath(accountId) : "/v3/accounts";
    const currencyIso = account ? getIsoCurrency(String(account.currency)) : "ARS";
    const { minDate, maxDate, initialDateRange } = useMemo(() => getDefaultDateRange(), []);
    const initialFetchDone = useRef(false);
    const [queryMeta, setQueryMeta] = useState(null);
    const [mobileHeaderRight, setMobileHeaderRight] = useState(null);

    const appliedDateRange = useMemo(() => {
        if (!dateFrom || !dateTo) {
            return null;
        }
        const from = dateFrom instanceof Date ? dateFrom : new Date(dateFrom);
        const to = dateTo instanceof Date ? dateTo : new Date(dateTo);
        return [from, to];
    }, [dateFrom, dateTo]);

    useEffect(() => {
        if (!accountId || !loggedUser?.userId) {
            return undefined;
        }
        dispatch(accountsActions.details(accountId, loggedUser.userId));
        initialFetchDone.current = false;
        return undefined;
    }, [dispatch, accountId, loggedUser?.userId]);

    useEffect(() => {
        if (!account?.idProduct || initialFetchDone.current) {
            return undefined;
        }
        initialFetchDone.current = true;
        const [from, to] = initialDateRange;
        dispatch(
            accountsActions.historicBalance({
                dateFrom: from,
                dateTo: to,
                selectedAccount: account,
                numberPage: 1,
            }),
        );
        return undefined;
    }, [account, dispatch, initialDateRange]);

    useEffect(() => {
        if (!fetchingBalance && historicBalances != null) {
            setQueryMeta(buildQueryMeta());
        }
    }, [fetchingBalance, historicBalances]);

    const handleBack = useCallback(() => {
        dispatch(push(goBackPath));
    }, [dispatch, goBackPath]);

    const handleSubmit = useCallback(
        (values) => {
            if (!account) {
                return;
            }
            const [from, to] = values.historicDates;
            dispatch(
                accountsActions.historicBalance({
                    dateFrom: from,
                    dateTo: to,
                    selectedAccount: account,
                    numberPage: 1,
                }),
            );
        },
        [account, dispatch],
    );

    const handlePageChange = useCallback(
        (pageNumber) => {
            if (!account) {
                return;
            }
            const from = dateFrom ?? initialDateRange[0];
            const to = dateTo ?? initialDateRange[1];
            dispatch(
                accountsActions.historicBalance({
                    dateFrom: from,
                    dateTo: to,
                    selectedAccount: account,
                    numberPage: pageNumber,
                }),
            );
        },
        [account, dateFrom, dateTo, dispatch, initialDateRange],
    );

    const handleExport = useCallback(
        (format) => {
            if (!account) {
                return;
            }
            const from = dateFrom ?? initialDateRange[0];
            const to = dateTo ?? initialDateRange[1];
            dispatch(accountsActions.downloadHistoricBalance(account.idProduct, format, from, to));
        },
        [account, dateFrom, dateTo, dispatch, initialDateRange],
    );

    const title = i18n.get("accounts.v3.historic.title");
    const subtitle = account
        ? `${i18n.get(`accounts.productType.${productTypes[account.productType]}`)} Nº ${maskedAccountNumber(
              account.number,
          )}`
        : "";

    const balanceLabel = getHistoricBalanceLabels("balanceLabel");

    const mappedRows = useMemo(() => {
        if (!historicBalances) {
            return [];
        }
        return historicBalances.map((item) => mapHistoricBalanceToRow(item, currencyIso, balanceLabel));
    }, [balanceLabel, currencyIso, historicBalances]);

    const listConfig = useMemo(
        () => ({
            emptyStateText: getHistoricBalanceLabels("empty"),
            paginationProps: buildHistoricBalancePagination(
                historicBalancesTotalPages,
                historicBalancesPageNumber,
                handlePageChange,
                mappedRows.length > 0,
            ),
        }),
        [handlePageChange, historicBalancesPageNumber, historicBalancesTotalPages, mappedRows.length],
    );

    useEffect(() => {
        if (!isUxDashboardImprovementsEnabled() || !account || isDesktop) {
            return undefined;
        }
        dispatchV3ShellLayout(dispatch, {
            headerTitle: title,
            headerSubtitle: subtitle,
            goBackPath,
            rightContentMobile: mobileHeaderRight,
            scopes: ["accounts", "historicbalance", "account/details"],
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [dispatch, account, isDesktop, title, subtitle, goBackPath, mobileHeaderRight]);

    const isLoading = fetching || !account || (fetchingBalance && historicBalances == null);

    return (
        <PageLoading loading={isLoading} className="screen-loader">
            {!isLoading && account && (
                <AccountHistoricBalance
                    goBack={handleBack}
                    title={title}
                    subtitle={subtitle}
                    getLabels={getHistoricBalanceLabels}
                    initialDateRange={initialDateRange}
                    minDate={minDate}
                    maxDate={maxDate}
                    onSubmit={handleSubmit}
                    onExport={handleExport}
                    exportLoading={fetchingDownload}
                    appliedDateRange={appliedDateRange}
                    onMobileHeaderRightContent={setMobileHeaderRight}
                    queryMeta={queryMeta}
                    list={listConfig}
                    rows={mappedRows}
                />
            )}
        </PageLoading>
    );
}

const mapStateToProps = (state) => ({
    loggedUser: sessionSelectors.getUser(state),
    account: accountsSelectors.getSelectedAccount(state),
    historicBalances: accountsSelectors.getHistoricBalances(state),
    historicBalancesTotalPages: accountsSelectors.getHistoricBalancesTotalPages(state),
    historicBalancesPageNumber: accountsSelectors.getHistoricBalancesPageNumber(state),
    dateFrom: accountsSelectors.getHistoricBalanceDateFrom(state),
    dateTo: accountsSelectors.getHistoricBalanceDateTo(state),
    fetching: accountsSelectors.getFetching(state),
    fetchingBalance: accountsSelectors.getFetchingBalance(state),
    fetchingDownload: accountsSelectors.getFetchingDownload(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(AccountHistoricBalanceV3Page);
